import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuth } from "../auth/AuthContext";
import { publicApi } from "../utils/publicApi";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import "./LandingPage.css";

const FEATURES = [
  {
    icon: "mdi:whatsapp",
    title: "Multi-session WhatsApp",
    text: "Connect several numbers, watch session health, and switch instances without leaving the workspace.",
  },
  {
    icon: "mdi:robot-outline",
    title: "Keyword automation",
    text: "Auto-reply to common questions with keyword rules, typing indicators, and per-session bots.",
  },
  {
    icon: "mdi:flash-outline",
    title: "Quick replies in inbox",
    text: "Save canned responses and insert them as chips while chatting — faster support, fewer typos.",
  },
  {
    icon: "mdi:bullhorn-outline",
    title: "Campaigns that scale",
    text: "Launch bulk sends with phonebooks, templates, random delays, and live queue / sent / failed logs.",
  },
  {
    icon: "mdi:message-text-outline",
    title: "Live customer inbox",
    text: "Reply in realtime, keep chat context in one place, and stop juggling phones for support.",
  },
  {
    icon: "mdi:bell-ring-outline",
    title: "Disconnect alerts",
    text: "Get an email when a WhatsApp session drops so you can reconnect before campaigns stall.",
  },
  {
    icon: "mdi:chart-box-outline",
    title: "Delivery analytics",
    text: "Dashboard charts for sends, hourly volume, campaigns, and instance health at a glance.",
  },
  {
    icon: "mdi:key-variant",
    title: "API-first sending",
    text: "Use your API key and client ID to trigger text, media, contacts, and more from your own stack.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Connect a session",
    text: "Create an instance, scan the QR from WhatsApp Linked Devices, and you’re online in minutes.",
  },
  {
    n: "02",
    title: "Message your way",
    text: "Chat live, run a campaign from a phonebook, or send programmatically with the API.",
  },
  {
    n: "03",
    title: "Track & improve",
    text: "Watch logs, remaining limits, and session status so every send stays under control.",
  },
];

const USE_CASES = [
  {
    icon: "mdi:storefront-outline",
    title: "Retail & delivery",
    text: "Order updates, driver coordination, and customer replies from one WhatsApp hub.",
  },
  {
    icon: "mdi:headset",
    title: "Support teams",
    text: "Shared inbox energy without the chaos — agents reply with full conversation context.",
  },
  {
    icon: "mdi:rocket-launch-outline",
    title: "Growth & agencies",
    text: "Run client campaigns with templates, delays, and clear delivery reporting.",
  },
];

const LandingPage = () => {
  const { isAuthenticated, userAuthenticated, adminAuthenticated } = useAuth();
  const location = useLocation();
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  const dashPath = userAuthenticated
    ? "/dashboard"
    : adminAuthenticated
      ? "/admin/dashboard"
      : "/dashboard";

  useEffect(() => {
    document.title = "AlphaXCloud — WhatsApp messaging platform";
    (async () => {
      try {
        const [faqRes, testiRes] = await Promise.all([
          publicApi({ url: "admin/get-all-faq" }).catch(() => null),
          publicApi({ url: "admin/get-all" }).catch(() => null),
        ]);
        if (faqRes?.success) setFaqs(faqRes.data || []);
        if (testiRes?.success) setTestimonials(testiRes.data || []);
      } catch {
        /* landing works without CMS */
      }
    })();
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location.hash, faqs, testimonials]);

  const testiRows = testimonials.length
    ? testimonials.slice(0, 4)
    : [
        {
          id: "f1",
          name: "Ayesha Khan",
          position: "Operations Lead",
          description:
            "We connected three WhatsApp lines in one afternoon. Campaigns that used to take a day now finish before lunch.",
        },
        {
          id: "f2",
          name: "Daniel Okoro",
          position: "Founder",
          description:
            "AlphaXCloud replaced our messy mix of personal phones and spreadsheets. The inbox is calm and the API is clear.",
        },
      ];

  const faqRows = faqs.length
    ? faqs.slice(0, 6)
    : [
        {
          id: "q1",
          que: "How do I connect a WhatsApp number?",
          ans: "Create an instance, open Scan, and scan the QR with WhatsApp Linked Devices. Your session stays available for chat, campaigns, and API sends.",
        },
        {
          id: "q2",
          que: "Can I send via API?",
          ans: "Yes. Generate an API key from your dashboard and send text, media, and more with your client ID.",
        },
      ];

  return (
    <div className="landing site-theme">
      <SiteHeader />

      <section className="landing-hero">
        <div className="landing-hero-bg" aria-hidden="true" />
        <div className="landing-hero-inner">
          <p className="landing-brand-mark">AlphaXCloud</p>
          <h1>WhatsApp messaging that ships at scale</h1>
          <p className="landing-hero-sub">
            Connect sessions, run campaigns, and talk to customers from one calm workspace.
          </p>
          <div className="landing-hero-actions">
            {isAuthenticated ? (
              <Link className="landing-btn landing-btn-primary" to={dashPath}>
                Open dashboard
              </Link>
            ) : (
              <>
                <Link className="landing-btn landing-btn-primary" to="/register">
                  Start free
                </Link>
                <Link className="landing-btn landing-btn-ghost" to="/login">
                  Login
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="landing-hero-visual" aria-hidden="true">
          <div className="landing-mock">
            <div className="landing-mock-bar">
              <span />
              <span />
              <span />
              <em>Live workspace</em>
            </div>
            <div className="landing-mock-row">
              <div className="landing-mock-wa">
                <Icon icon="mdi:whatsapp" width={22} />
              </div>
              <div>
                <strong>3 sessions online</strong>
                <small>Sales · Support · Campaigns</small>
              </div>
              <span className="landing-mock-dot" />
            </div>
            <div className="landing-mock-bubble in">Campaign queued — 1,240 contacts</div>
            <div className="landing-mock-bubble out">Hi Sara — your order ships today.</div>
            <div className="landing-mock-bubble in">Inbox synced · 18 unread</div>
            <div className="landing-mock-meta">
              <span>API ready</span>
              <span>Logs live</span>
              <span>Multi-instance</span>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-how" id="how">
        <div className="landing-section-inner">
          <p className="landing-eyebrow">How it works</p>
          <h2>From QR scan to first campaign in three steps</h2>
          <p className="landing-section-sub">
            No complicated stack. Connect WhatsApp, choose how you send, and stay in control with clear logs.
          </p>
          <div className="landing-steps">
            {STEPS.map((s) => (
              <article key={s.n} className="landing-step">
                <span className="landing-step-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-alt" id="features">
        <div className="landing-section-inner">
          <p className="landing-eyebrow">Platform</p>
          <h2>Everything operators need — in one place</h2>
          <p className="landing-section-sub">
            Sessions, inbox, automation, quick replies, campaigns, analytics, and API access — designed to work together.
          </p>
          <div className="landing-features">
            {FEATURES.map((f) => (
              <article key={f.title} className="landing-feature">
                <div className="landing-feature-icon">
                  <Icon icon={f.icon} width={24} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="use-cases">
        <div className="landing-section-inner">
          <p className="landing-eyebrow">Who it’s for</p>
          <h2>Built for teams that live in WhatsApp</h2>
          <p className="landing-section-sub">
            Whether you support customers or run outbound at volume, AlphaXCloud keeps messaging organized.
          </p>
          <div className="landing-usecases">
            {USE_CASES.map((u) => (
              <article key={u.title} className="landing-usecase">
                <div className="landing-feature-icon">
                  <Icon icon={u.icon} width={24} />
                </div>
                <h3>{u.title}</h3>
                <p>{u.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-alt landing-testi-section" id="testimonials">
        <div className="landing-section-inner">
          <p className="landing-testi-eyebrow">Voices</p>
          <h2>Trusted by teams that message with care</h2>
          <p className="landing-section-sub">
            Quiet confidence from operators and founders running WhatsApp on AlphaXCloud.
          </p>
          <div className="landing-quotes">
            {testiRows.map((t, i) => {
              const parts = String(t.name || "")
                .trim()
                .split(/\s+/)
                .filter(Boolean);
              const av =
                parts.length >= 2
                  ? (parts[0][0] + parts[1][0]).toUpperCase()
                  : (parts[0]?.[0] || "A").toUpperCase();
              return (
                <blockquote
                  key={t.id}
                  className="landing-quote"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <span className="landing-quote-mark" aria-hidden="true">
                    “
                  </span>
                  <p>{t.description}</p>
                  <footer>
                    <span className="landing-quote-avatar" aria-hidden="true">
                      {av}
                    </span>
                    <span>
                      <strong>{t.name}</strong>
                      {t.position ? <em>{t.position}</em> : null}
                    </span>
                  </footer>
                </blockquote>
              );
            })}
          </div>
          {testimonials.length > 4 ? (
            <p className="landing-more">
              <Link to="/testimonials">Read more stories</Link>
            </p>
          ) : (
            <p className="landing-more">
              <Link to="/testimonials">Browse testimonials</Link>
            </p>
          )}
        </div>
      </section>

      <section className="landing-section landing-section-alt" id="pricing">
        <div className="landing-section-inner landing-pricing-teaser">
          <p className="landing-eyebrow">Pricing</p>
          <h2>Transparent plans for every volume</h2>
          <p className="landing-section-sub">
            Compare sessions, message limits, and API access — then order from your workspace.
          </p>
          <Link className="landing-btn landing-btn-primary" to="/pricing">
            View pricing
          </Link>
        </div>
      </section>

      <section className="landing-section" id="faq">
        <div className="landing-section-inner landing-faq-wrap">
          <div className="landing-faq-intro">
            <p className="landing-eyebrow">FAQ</p>
            <h2>Answers before you get stuck</h2>
            <p className="landing-section-sub">
              Clear guidance on sessions, campaigns, limits, and API access.
            </p>
            <Link className="landing-btn landing-btn-ghost" to="/faqs">
              View all FAQs
            </Link>
          </div>
          <div className="landing-faq-list">
            {faqRows.map((item) => (
              <details key={item.id} className="landing-faq">
                <summary>{item.que}</summary>
                <p>{item.ans}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta-band">
        <div className="landing-cta-band-inner">
          <h2>Ready to message smarter?</h2>
          <p>
            Create your workspace, connect a WhatsApp session, and send your first campaign today.
          </p>
          {isAuthenticated ? (
            <Link className="landing-btn landing-btn-primary" to={dashPath}>
              Go to dashboard
            </Link>
          ) : (
            <div className="landing-hero-actions">
              <Link className="landing-btn landing-btn-primary" to="/register">
                Create account
              </Link>
              <Link className="landing-btn landing-btn-ghost" to="/login">
                Login
              </Link>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
